import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { useHasPermission } from "../../../access/use-access"
import type { CatalogView, ServiceView } from "../../../api/directory.api"
import { fetchCatalog, upsertService } from "../../../api/directory.api"
import { isApiProblem } from "../../../api/problem"
import { cx } from "../../../lib/cx"
import { minutesLabel } from "../../../lib/plural"
import { ProblemScreen } from "../../../shell/states"
import { Badge, Button, Skeleton, Switch } from "../../../ui"
import { formatMoney } from "../format-money"
import { useResource } from "../use-resource"
import {
  type ServiceForm,
  type StepDraft,
  blankStep,
  coloringPreset,
  formOf,
  futureNote,
  moveStep,
  noPauseQuestion,
  pauseMinutes,
  requestOf,
  stepsProblem,
  timelineOf,
  totalMinutes,
} from "./service-form"
import styles from "./steps.module.css"

/**
 * 3-Б3 «Составная услуга с паузой» — /catalog/:serviceId/steps.
 *
 * ГЛАВНОЕ НА ЭКРАНЕ — ПОЛОСА ВРЕМЕНИ, а не таблица шагов: две дорожки, «мастер» и «кресло»,
 * и на дорожке мастера в середине — окно. Одного взгляда хватает, чтобы понять механику
 * «пока краска держится, мастер свободен, кресло занято»; текстом это объясняется хуже.
 * Рядом — ответ «и что мне с этого» в деньгах салона: окно — это 1–2 записи за смену.
 *
 * Длительность услуги не вводится — она сумма шагов. Зазоров нет: пауза — тоже шаг.
 */
export function StepsScreen(props: {
  readonly serviceId: string
  readonly onBack: () => void
}): ReactNode {
  const canManage = useHasPermission("catalog.services.manage")
  const catalog = useResource((signal) => fetchCatalog(signal), "catalog-steps")
  const service = catalog.data?.services.find((item) => item.id === props.serviceId) ?? null

  const [form, setForm] = useState<ServiceForm | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [saved, setSaved] = useState<string | null>(null)

  useEffect(() => {
    if (service !== null && form === null) {
      const base = formOf(service)
      setForm({ ...base, kind: "composite" })
    }
  }, [service, form])

  if (catalog.problem !== null && catalog.data === null) {
    return <ProblemScreen problem={catalog.problem} onRetry={catalog.reload} />
  }
  if (catalog.data !== null && service === null) {
    return (
      <div className={styles.screen}>
        <div className={styles.card}>
          <button type="button" className={styles.back} onClick={props.onBack}>
            ‹ К каталогу
          </button>
          <div className={styles.title}>Такой услуги нет в каталоге</div>
        </div>
      </div>
    )
  }
  if (service === null || form === null || catalog.data === null) {
    return (
      <div className={styles.screen}>
        <div className={styles.card}>
          <Skeleton width="60%" height={18} radius={4} />
          <Skeleton width="100%" height={160} radius={8} />
        </div>
      </div>
    )
  }

  const steps = form.steps
  const setSteps = (next: readonly StepDraft[]): void => {
    setForm({ ...form, steps: next })
    setSaved(null)
    setProblem(null)
  }
  const patchStep = (index: number, patch: Partial<StepDraft>): void => {
    setSteps(steps.map((step, i) => (i === index ? { ...step, ...patch } : step)))
  }

  const total = totalMinutes(steps)
  const pause = pauseMinutes(steps)
  const error = steps.length === 0 ? null : stepsProblem(steps)
  const question = error === null ? noPauseQuestion(steps) : null
  const timeline = timelineOf(steps)
  const denied = canManage ? undefined : "Править каталог может управляющий или владелец"

  const save = async (next: ServiceForm): Promise<void> => {
    setBusy(true)
    setProblem(null)
    try {
      await upsertService(requestOf(next, service))
      setSaved(next.kind === "composite" ? "Шаги сохранены. Новые записи лягут по ним." : "Услуга стала простой.")
      setForm(null)
      catalog.reload()
      if (next.kind === "simple") props.onBack()
    } catch (caught) {
      if (!isApiProblem(caught)) throw caught
      setProblem(
        caught.status === 409
          ? "Услугу изменили, пока вы правили шаги. Обновите страницу — ваши шаги придётся внести ещё раз."
          : (caught.detail ?? caught.title),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.head}>
          <button type="button" className={styles.back} onClick={props.onBack}>
            ‹ К каталогу
          </button>
        </div>
        <div className={styles.head}>
          <div>
            <div className={styles.title}>{service.name}</div>
            <div className={styles.sub}>{subtitleOf(service, catalog.data)}</div>
          </div>
          <Badge tone="info" className={styles.total}>
            Всего {total} мин — считается из шагов
          </Badge>
        </div>

        <div className={styles.steps}>
          <div className={cx(styles.grid, styles.gridHead)} aria-hidden="true">
            <span />
            <span>Шаг</span>
            <span>Минут</span>
            <span>Мастер занят</span>
            <span>Кресло занято</span>
            <span />
          </div>
          {steps.length === 0 ? (
            <div className={styles.note}>
              Шагов пока нет. Начните с готового набора окрашивания — по нему видно, как принято заполнять: пауза в
              середине, кресло занято всё время.
            </div>
          ) : null}
          {steps.map((step, index) => {
            const bad = !step.occupiesEmployee && !step.occupiesResource
            const n = index + 1
            return (
              <div key={step.key} className={cx(styles.grid, styles.step, bad && styles.stepBad)}>
                <div className={cx(styles.handle, styles.aHandle)}>
                  <button
                    type="button"
                    className={styles.move}
                    aria-label={`Шаг ${n} выше`}
                    disabled={index === 0}
                    onClick={() => setSteps(moveStep(steps, index, -1))}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    className={styles.move}
                    aria-label={`Шаг ${n} ниже`}
                    disabled={index === steps.length - 1}
                    onClick={() => setSteps(moveStep(steps, index, 1))}
                  >
                    ▼
                  </button>
                </div>
                <input
                  className={cx(styles.input, styles.aName)}
                  aria-label={`Название шага ${n}`}
                  value={step.name}
                  maxLength={200}
                  onChange={(event) => patchStep(index, { name: event.target.value })}
                />
                <input
                  className={cx(styles.input, styles.minutes, styles.aMinutes)}
                  aria-label={`Минут на шаге ${n}`}
                  inputMode="numeric"
                  value={step.minutes}
                  onChange={(event) => patchStep(index, { minutes: event.target.value.replace(/[^\d]/g, "") })}
                />
                <Switch
                  className={styles.aEmp}
                  checked={step.occupiesEmployee}
                  onChange={(checked) => patchStep(index, { occupiesEmployee: checked })}
                >
                  <span className={styles.srOnly}>Мастер занят на шаге {n}</span>
                </Switch>
                <Switch
                  className={styles.aRes}
                  checked={step.occupiesResource}
                  onChange={(checked) => patchStep(index, { occupiesResource: checked })}
                >
                  <span className={styles.srOnly}>Кресло занято на шаге {n}</span>
                </Switch>
                <button
                  type="button"
                  className={cx(styles.remove, styles.aRemove)}
                  aria-label={`Удалить шаг ${n}`}
                  onClick={() => setSteps(steps.filter((_, i) => i !== index))}
                >
                  ✕
                </button>
              </div>
            )
          })}
          <Button
            size="sm"
            variant="ghost"
            className={styles.addRow}
            disabled={steps.length >= 20}
            onClick={() => setSteps([...steps, blankStep()])}
          >
            + Добавить шаг
          </Button>
        </div>

        {error === null ? null : (
          <div className={styles.error} role="alert">
            {error}
          </div>
        )}
        {question === null ? null : (
          <div className={styles.question}>
            {question}
            <Button
              size="sm"
              deniedReason={denied}
              loading={busy}
              onClick={() =>
                void save({ ...form, kind: "simple", minutes: String(total), steps: [] })
              }
            >
              Сделать простой · {total} мин
            </Button>
          </div>
        )}

        {timeline.cells.length === 0 ? null : (
          <div>
            <div className={styles.timelineTitle}>Как это выглядит в расписании</div>
            <div className={styles.timeline}>
              <span />
              <div className={styles.ticks} aria-hidden="true">
                {timeline.ticks.map((tick) => (
                  <span key={tick.label} className={styles.tick} style={{ left: `${tick.left}%` }}>
                    {tick.label}
                  </span>
                ))}
              </div>
              <span className={styles.laneLabel}>Мастер</span>
              <div className={styles.lane}>
                {timeline.cells.map((cell) => (
                  <div
                    key={cell.key}
                    className={cx(styles.cell, cell.employee ? styles.busyEmployee : styles.free)}
                    style={{ width: `${cell.percent}%` }}
                    title={`${cell.name}: ${cell.minutes} мин, мастер ${cell.employee ? "занят" : "свободен"}`}
                  >
                    {cell.employee ? cell.name : `свободен · ${cell.minutes} мин`}
                  </div>
                ))}
              </div>
              <span className={styles.laneLabel}>Кресло</span>
              <div className={styles.lane}>
                {timeline.cells.map((cell) => (
                  <div
                    key={cell.key}
                    className={cx(styles.cell, cell.resource ? styles.busyResource : styles.free)}
                    style={{ width: `${cell.percent}%` }}
                    title={`${cell.name}: ${cell.minutes} мин, кресло ${cell.resource ? "занято" : "свободно"}`}
                  >
                    {cell.resource ? "занято" : "свободно"}
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.legend}>
              <span>
                <span className={cx(styles.swatch, styles.swatchBusy)} aria-hidden="true" />
                занят
              </span>
              <span>
                <span className={cx(styles.swatch, styles.swatchFree)} aria-hidden="true" />
                свободен — сюда можно записать другого клиента
              </span>
            </div>
          </div>
        )}

        {pause > 0 && error === null ? (
          <div className={styles.pause}>
            <b>Мастер сможет взять другого клиента в окне {minutesLabel(pause)}.</b> За смену это 1–2 дополнительные записи.
            {steps.some((step) => !step.occupiesEmployee && step.occupiesResource)
              ? " Кресло при этом занято — второго клиента в него система не посадит."
              : ""}
          </div>
        ) : null}

        <div className={styles.note}>
          Пауза — это тоже шаг: добавьте «Выдержку» и снимите отметку «мастер занят». Зазоров между шагами не бывает.{" "}
          {futureNote(service.futureAppointments, service.name)}
        </div>

        {problem === null ? null : (
          <div className={styles.error} role="alert">
            {problem}
          </div>
        )}
        {saved === null ? null : (
          <div className={styles.pause} role="status">
            {saved}
          </div>
        )}

        <div className={styles.actions}>
          <Button
            variant="primary"
            loading={busy}
            deniedReason={denied ?? (steps.length === 0 ? "Добавьте шаги" : (error ?? undefined))}
            onClick={() => void save(form)}
          >
            Сохранить
          </Button>
          <Button deniedReason={denied} onClick={() => setSteps(coloringPreset())}>
            Готовый набор шагов окрашивания
          </Button>
        </div>
      </div>
    </div>
  )
}

function subtitleOf(service: ServiceView, catalog: CatalogView): string {
  const category = catalog.categories.find((item) => item.id === service.categoryId)?.name
  return [
    "Составная",
    category ?? null,
    service.branchId === null ? "все филиалы" : "один филиал",
    formatMoney(service.priceMinor),
  ]
    .filter((part): part is string => part !== null && part !== undefined)
    .join(" · ")
}

